import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Ham zaman serisi kayıtları (tank okuması, pompa satışı, tabanca endeksi) ve bunların okuma uçları.
 * Ölçüler DİZGEdir (float yok, AGENTS.md §3.2); zamanlar ISO 8601 UTC'dir.
 */
// ── Ortak sözlükler ──────────────────────────────────────────────────────────
export const TANK_READING_SOURCES = ["PROBE", "MANUAL", "DELIVERY"];
export const tankReadingSourceSchema = z.enum(TANK_READING_SOURCES);
export const TANK_READING_SOURCE_LABELS = {
    PROBE: "Tank probu",
    MANUAL: "Elle ölçüm",
    DELIVERY: "Dolum ölçümü",
};
/** Alımda istemcinin gönderebileceği kaynaklar (`DELIVERY` dolum belgesinden yazılır). */
export const ingestTankReadingSourceSchema = z.enum(["PROBE", "MANUAL"]);
export const TOTALIZER_READING_SOURCES = ["AGENT", "MANUAL", "DELIVERY", "ADJUSTMENT"];
export const totalizerReadingSourceSchema = z.enum(TOTALIZER_READING_SOURCES);
export const TOTALIZER_READING_SOURCE_LABELS = {
    AGENT: "İstasyon ajanı",
    MANUAL: "Elle okuma",
    DELIVERY: "Dolum ölçümü",
    ADJUSTMENT: "Onaylı endeks düzeltmesi",
};
export const ingestTotalizerSourceSchema = z.enum(["AGENT", "MANUAL"]);
export const FUEL_PAYMENT_TYPES = [
    "CASH",
    "CARD",
    "ON_ACCOUNT",
    "FLEET",
    "LOYALTY",
    "MIXED",
    "OTHER",
    "NONE",
];
export const fuelPaymentTypeSchema = z.enum(FUEL_PAYMENT_TYPES);
export const FUEL_PAYMENT_TYPE_LABELS = {
    CASH: "Nakit",
    CARD: "Kredi / banka kartı",
    ON_ACCOUNT: "Cari hesaba",
    FLEET: "Filo / TTS",
    LOYALTY: "Sadakat",
    MIXED: "Karışık",
    OTHER: "Diğer",
    NONE: "Yok (test çekişi)",
};
export const FUEL_TRANSACTION_SOURCES = ["AGENT", "API", "IMPORT", "MANUAL"];
export const fuelTransactionSourceSchema = z.enum(FUEL_TRANSACTION_SOURCES);
export const FUEL_TRANSACTION_SOURCE_LABELS = {
    AGENT: "İstasyon ajanı",
    API: "API",
    IMPORT: "İçe aktarma",
    MANUAL: "Elle giriş",
};
/** Tank seviyesi (mm) ve su seviyesi: numeric(10,2). */
export const millimetreSchema = z
    .string()
    .regex(/^\d{1,8}(\.\d{1,2})?$/, "Seviye en fazla 2 ondalık basamaklı, negatif olmayan bir sayı olmalıdır")
    .describe('Milimetre (dizge, en fazla 2 ondalık). Örn. "1842.50"');
/** Sıcaklık: numeric(5,2), −40…80 °C (sensör hatası −999 gibi değerler elenir). */
export const temperatureSchema = z
    .string()
    .regex(/^-?\d{1,3}(\.\d{1,2})?$/, "Sıcaklık en fazla 2 ondalık basamaklı bir sayı olmalıdır")
    .refine((value) => Number(value) >= -40 && Number(value) <= 80, "Sıcaklık −40…80 °C aralığında olmalıdır")
    .describe('Santigrat derece (dizge, en fazla 2 ondalık). Örn. "17.40"');
// ── Elle okuma girişi (pompacı / kasiyer ekranı) ─────────────────────────────
export const createTankReadingSchema = z.object({
    tankId: uuidSchema,
    measuredAt: timestampSchema,
    levelMm: millimetreSchema,
    waterMm: millimetreSchema.nullable().optional(),
    temperatureC: temperatureSchema.nullable().optional(),
});
export const createTotalizerReadingSchema = z.object({
    nozzleId: uuidSchema,
    readAt: timestampSchema,
    electronicL: quantitySchema,
    mechanicalL: quantitySchema.nullable().optional(),
});
// ── Tank okuması (yanıt) ─────────────────────────────────────────────────────
export const tankReadingSchema = z.object({
    id: uuidSchema,
    tankId: uuidSchema,
    stationId: uuidSchema,
    measuredAt: timestampSchema,
    levelMm: millimetreSchema,
    waterMm: millimetreSchema.nullable(),
    temperatureC: temperatureSchema.nullable(),
    /** Kalibrasyon tablosundan hesaplanan ortam hacmi (L). */
    grossVolumeL: quantitySchema,
    /** 15 °C'ye indirgenmiş hacim; sıcaklık ya da ürünün β'sı yoksa null. */
    netVolumeL: quantitySchema.nullable(),
    /** LPG gibi kütleyle izlenen ürünlerde brüt hacmin kg karşılığı (SAKLANMAZ, hesaplanır). */
    massKg: quantitySchema.nullable(),
    strappingTableId: uuidSchema.nullable(),
    source: tankReadingSourceSchema,
    recordedBy: uuidSchema.nullable(),
});
// ── Tank durumu ekranı ───────────────────────────────────────────────────────
export const tankStatusSchema = z.object({
    tankId: uuidSchema,
    tankNo: z.number().int(),
    stationId: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    productColor: z.string().nullable(),
    isLpg: z.boolean(),
    nominalCapacityL: quantitySchema,
    safeFillPercent: rateSchema,
    /** Kapasite × güvenli doluluk yüzdesi (L). Dolum önerisi bu sınıra kadar yapılır. */
    safeFillVolumeL: quantitySchema,
    minLevelL: quantitySchema,
    criticalLevelL: quantitySchema,
    status: z.enum(["ACTIVE", "OUT_OF_SERVICE", "DECOMMISSIONED"]),
    lastReading: tankReadingSchema.nullable(),
    /** Son okumanın kapasiteye oranı (%); okuma yoksa null. */
    fillPercent: rateSchema.nullable(),
    belowMinLevel: z.boolean(),
    belowCriticalLevel: z.boolean(),
    aboveSafeFill: z.boolean(),
    /** Tahmini bitiş zamanı — D aşamasında (tüketim profili) doldurulacak; şimdilik null. */
    estimatedRunOutAt: timestampSchema.nullable(),
});
export const tankStatusListSchema = z.object({
    stationId: uuidSchema,
    items: z.array(tankStatusSchema),
});
export const stationParamsSchema = z.object({ stationId: uuidSchema });
export const tankParamsSchema = z.object({ tankId: uuidSchema });
export const nozzleParamsSchema = z.object({ nozzleId: uuidSchema });
// ── Seviye grafiği ───────────────────────────────────────────────────────────
export const TANK_SERIES_RESOLUTIONS = ["RAW", "HOURLY", "DAILY"];
export const tankSeriesResolutionSchema = z.enum(TANK_SERIES_RESOLUTIONS);
export const TANK_SERIES_RESOLUTION_LABELS = {
    RAW: "Ham okumalar",
    HOURLY: "Saatlik",
    DAILY: "Günlük",
};
/** RAW çözünürlükte bir istekte dönebilecek en çok nokta; aşılırsa 400 (aralığı daraltın ya da HOURLY kullanın). */
export const MAX_TANK_SERIES_POINTS = 5000;
export const tankReadingSeriesQuerySchema = z.object({
    from: timestampSchema,
    to: timestampSchema,
    resolution: tankSeriesResolutionSchema.default("RAW"),
});
export const tankSeriesPointSchema = z.object({
    at: timestampSchema,
    grossVolumeL: quantitySchema,
    netVolumeL: quantitySchema.nullable(),
    levelMm: millimetreSchema.nullable(),
    waterMm: millimetreSchema.nullable(),
    temperatureC: temperatureSchema.nullable(),
    /** DAILY çözünürlükte günün en düşük / en yüksek hacmi; diğerlerinde null. */
    minVolumeL: quantitySchema.nullable(),
    maxVolumeL: quantitySchema.nullable(),
});
export const tankReadingSeriesSchema = z.object({
    tankId: uuidSchema,
    resolution: tankSeriesResolutionSchema,
    from: timestampSchema,
    to: timestampSchema,
    points: z.array(tankSeriesPointSchema),
});
// ── Satış işlemleri ──────────────────────────────────────────────────────────
export const fuelTransactionSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    nozzleId: uuidSchema,
    tankId: uuidSchema,
    productId: uuidSchema,
    occurredAt: timestampSchema,
    businessDate: localDateSchema,
    litres: quantitySchema,
    unitPrice: unitPriceSchema,
    amount: moneySchema,
    paymentType: fuelPaymentTypeSchema,
    shiftId: uuidSchema.nullable(),
    attendantUserId: uuidSchema.nullable(),
    /** KVKK: kişisel veri olabilir — YALNIZCA bu yanıtta döner, günlüğe / olaya / audit'e yazılmaz. */
    vehiclePlate: z.string().nullable(),
    vehicleTagId: z.string().nullable(),
    counterpartyId: uuidSchema.nullable(),
    fiscalReceiptRef: z.string().nullable(),
    agentTxId: z.string(),
    isTest: z.boolean(),
    testReturnedToTank: z.boolean(),
    totalizerStart: quantitySchema.nullable(),
    totalizerEnd: quantitySchema.nullable(),
    source: fuelTransactionSourceSchema,
});
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
export const listFuelTransactionsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
    nozzleId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    paymentType: fuelPaymentTypeSchema.optional(),
    isTest: queryBoolean.optional(),
});
export const fuelTransactionPageSchema = pageOf(fuelTransactionSchema);
// ── Endeks okumaları ─────────────────────────────────────────────────────────
export const totalizerReadingSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    nozzleId: uuidSchema,
    readAt: timestampSchema,
    electronicL: quantitySchema,
    mechanicalL: quantitySchema.nullable(),
    source: totalizerReadingSourceSchema,
    adjustmentId: uuidSchema.nullable(),
    recordedBy: uuidSchema.nullable(),
});
export const listTotalizerReadingsQuerySchema = pageQuerySchema.extend({
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
    source: totalizerReadingSourceSchema.optional(),
});
export const totalizerReadingPageSchema = pageOf(totalizerReadingSchema);
//# sourceMappingURL=readings.js.map